/**
 * SEO-Utility: Setzt seitenspezifische Meta-Tags via DOM
 * Gibt eine Cleanup-Funktion zurück, die die vorherigen Werte wiederherstellt.
 */

const BASE_DOMAIN = "https://barometer.werbeagentur.me";

interface SeoConfig {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  noindex?: boolean;
}

function setMetaTag(name: string, content: string, isProperty = false): () => void {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  const prev = el?.content ?? null;
  const wasNew = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
  return () => {
    if (wasNew) {
      el?.parentNode?.removeChild(el);
    } else if (prev !== null && el) {
      el.content = prev;
    }
  };
}

export function applySeo(config: SeoConfig): () => void {
  const { title, description, path, ogImage, noindex } = config;
  const url = `${BASE_DOMAIN}${path}`;
  const image = ogImage ?? `${BASE_DOMAIN}/og-trenchcoat.jpg`;

  const prevTitle = document.title;
  document.title = title;

  const cleanups = [
    setMetaTag("description", description),
    setMetaTag("og:title", title, true),
    setMetaTag("og:description", description, true),
    setMetaTag("og:url", url, true),
    setMetaTag("og:image", image, true),
    setMetaTag("og:type", "website", true),
    setMetaTag("og:locale", "de_DE", true),
    setMetaTag("twitter:card", "summary_large_image"),
    setMetaTag("twitter:title", title),
    setMetaTag("twitter:description", description),
    setMetaTag("twitter:image", image),
  ];

  // noindex
  if (noindex) {
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'noindex, nofollow';
  }

  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  const prevCanonical = canonical?.href ?? null;
  const canonicalWasNew = !canonical;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = url;

  // hreflang
  let hreflang = document.querySelector('link[hreflang="de"]') as HTMLLinkElement | null;
  const hreflangWasNew = !hreflang;
  const prevHreflang = hreflang?.href ?? null;
  if (!hreflang) {
    hreflang = document.createElement("link");
    hreflang.rel = "alternate";
    hreflang.setAttribute("hreflang", "de");
    document.head.appendChild(hreflang);
  }
  hreflang.href = url;

  return () => {
    document.title = prevTitle;
    cleanups.forEach((fn) => fn());
    if (canonicalWasNew) {
      canonical?.parentNode?.removeChild(canonical);
    } else if (prevCanonical !== null && canonical) {
      canonical.href = prevCanonical;
    }
    if (hreflangWasNew) {
      hreflang?.parentNode?.removeChild(hreflang);
    } else if (prevHreflang !== null && hreflang) {
      hreflang.href = prevHreflang;
    }
  };
}
